import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, Plus, Download, Trash2, Loader2, Upload, Briefcase, Receipt, Calendar, FolderOpen,
  Send, Users, Search, ShieldCheck, Shield, X, Check, User
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'contracts', label: 'Contrats', icon: Briefcase, color: 'bg-blue-100 text-blue-700' },
  { value: 'payslips', label: 'Fiches de paie', icon: Receipt, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'leave', label: 'Congés', icon: Calendar, color: 'bg-amber-100 text-amber-700' },
  { value: 'other', label: 'Autres', icon: FolderOpen, color: 'bg-purple-100 text-purple-700' }
];

export default function DocumentsPage() {
  const { isAdmin, token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchUser, setSearchUser] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  
  const [form, setForm] = useState({ name: '', category: '', description: '', file: null });
  const [sendForm, setSendForm] = useState({ name: '', category: '', description: '', file: null });

  useEffect(() => {
    fetchDocuments();
    if (isAdmin) {
      fetchUsers();
    }
  }, [selectedCategory, isAdmin]);

  const fetchDocuments = async () => {
    try {
      const params = selectedCategory ? `?category=${selectedCategory}` : '';
      const res = await axios.get(`${API_URL}/api/documents${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chat/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10MB');
      return;
    }
    setForm({ ...form, file });
  };

  const handleSendFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10MB');
      return;
    }
    setSendForm({ ...sendForm, file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      let fileData = null, fileType = null;
      if (form.file) {
        const reader = new FileReader();
        fileData = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(form.file);
        });
        fileType = form.file.type;
      }
      await axios.post(`${API_URL}/api/documents`, { 
        name: form.name, 
        category: form.category, 
        description: form.description, 
        file_data: fileData, 
        file_type: fileType 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document ajouté');
      setDialogOpen(false);
      setForm({ name: '', category: '', description: '', file: null });
      fetchDocuments();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendDocument = async (e) => {
    e.preventDefault();
    if (!sendForm.name || !sendForm.category) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!sendToAll && selectedRecipients.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }
    if (!sendForm.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setSubmitting(true);
    try {
      let fileData = null, fileType = null;
      if (sendForm.file) {
        const reader = new FileReader();
        fileData = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(sendForm.file);
        });
        fileType = sendForm.file.type;
      }

      await axios.post(`${API_URL}/api/documents/send`, {
        name: sendForm.name,
        category: sendForm.category,
        description: sendForm.description,
        file_data: fileData,
        file_type: fileType,
        recipients: selectedRecipients.map(u => u.id),
        send_to_all: sendToAll
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const recipientCount = sendToAll ? users.length : selectedRecipients.length;
      toast.success(`Document envoyé à ${recipientCount} destinataire(s)`);
      setSendDialogOpen(false);
      setSendForm({ name: '', category: '', description: '', file: null });
      setSelectedRecipients([]);
      setSendToAll(false);
      fetchDocuments();
    } catch (error) {
      console.error('Failed to send document:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.file_data) {
        const link = document.createElement('a');
        link.href = `data:${res.data.file_type};base64,${res.data.file_data}`;
        link.download = res.data.name;
        link.click();
      } else {
        toast.info('Ce document n\'a pas de fichier attaché');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.name}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document supprimé');
      fetchDocuments();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleRecipient = (user) => {
    if (selectedRecipients.find(r => r.id === user.id)) {
      setSelectedRecipients(selectedRecipients.filter(r => r.id !== user.id));
    } else {
      setSelectedRecipients([...selectedRecipients, user]);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const filteredUsers = users.filter(u => 
    `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(searchUser.toLowerCase())
  );

  const getCategoryConfig = (value) => categories.find(c => c.value === value) || categories[3];
  
  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Documents</h1>
          <p className="text-slate-500 mt-1">Gérez vos documents RH</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button 
              onClick={() => setSendDialogOpen(true)}
              variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
              data-testid="send-document-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Envoyer un document
            </Button>
          )}
          <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-document-btn">
            <Plus className="w-4 h-4 mr-2" />Ajouter
          </Button>
        </div>
      </div>

      {/* Categories */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {categories.map((cat) => {
          const Icon = cat.icon;
          const count = documents.filter(d => d.category === cat.value).length;
          const isSelected = selectedCategory === cat.value;
          return (
            <Card 
              key={cat.value}
              className={`cursor-pointer transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-slate-900' : ''}`}
              onClick={() => setSelectedCategory(isSelected ? null : cat.value)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className={`p-2 rounded-lg ${cat.color.split(' ')[0]}`}>
                    <Icon className={`w-5 h-5 ${cat.color.split(' ')[1]}`} />
                  </div>
                  <Badge variant="secondary">{count}</Badge>
                </div>
                <h3 className="font-medium mt-3">{cat.label}</h3>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Documents list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {selectedCategory ? getCategoryConfig(selectedCategory).label : 'Tous les documents'}
            {selectedCategory && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedCategory(null)} className="ml-auto">Voir tout</Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aucun document</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => {
                const catConfig = getCategoryConfig(doc.category);
                const CatIcon = catConfig.icon;
                return (
                  <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg border hover:bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${catConfig.color.split(' ')[0]}`}>
                        <CatIcon className={`w-5 h-5 ${catConfig.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span>{catConfig.label}</span>
                          <span>•</span>
                          <span>{formatFileSize(doc.file_size)}</span>
                          <span>•</span>
                          <span>{format(new Date(doc.created_at), 'dd MMM yyyy', { locale: fr })}</span>
                          {doc.sender_name && (
                            <>
                              <span>•</span>
                              <span className="text-blue-600">De: {doc.sender_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)} title="Télécharger">
                        <Download className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(doc)} className="text-destructive" title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Document Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajouter un document</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Catégorie *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Fichier</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input type="file" onChange={handleFileChange} className="hidden" id="file-input" />
                <label htmlFor="file-input" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  {form.file ? <p className="text-sm font-medium">{form.file.name}</p> : <p className="text-sm text-slate-500">Cliquez pour sélectionner</p>}
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Document Dialog */}
      {isAdmin && (
        <Dialog open={sendDialogOpen} onOpenChange={(open) => {
          setSendDialogOpen(open);
          if (!open) {
            setSendForm({ name: '', category: '', description: '', file: null });
            setSelectedRecipients([]);
            setSendToAll(false);
            setSearchUser('');
          }
        }}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="send-document-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Envoyer un document
              </DialogTitle>
              <DialogDescription>
                Envoyez un document à un ou plusieurs utilisateurs
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSendDocument} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom du document *</Label>
                <Input
                  value={sendForm.name}
                  onChange={(e) => setSendForm({ ...sendForm, name: e.target.value })}
                  placeholder="Ex: Fiche de paie - Janvier 2026"
                  data-testid="send-document-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select value={sendForm.category} onValueChange={(v) => setSendForm({ ...sendForm, category: v })}>
                  <SelectTrigger data-testid="send-document-category-select">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={sendForm.description}
                  onChange={(e) => setSendForm({ ...sendForm, description: e.target.value })}
                  placeholder="Description optionnelle..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Fichier *</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-slate-300 transition-colors">
                  <input type="file" onChange={handleSendFileChange} className="hidden" id="send-file-input" data-testid="send-document-file-input" />
                  <label htmlFor="send-file-input" className="cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                    {sendForm.file ? (
                      <p className="text-sm text-slate-700 font-medium">{sendForm.file.name}</p>
                    ) : (
                      <>
                        <p className="text-sm text-slate-600">Cliquez pour sélectionner un fichier</p>
                        <p className="text-xs text-slate-400 mt-1">Max. 10MB</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Recipients selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Destinataires *</Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="send-to-all"
                      checked={sendToAll}
                      onCheckedChange={(checked) => {
                        setSendToAll(checked);
                        if (checked) setSelectedRecipients([]);
                      }}
                    />
                    <label htmlFor="send-to-all" className="text-sm text-slate-600 cursor-pointer">
                      Envoyer à tous
                    </label>
                  </div>
                </div>

                {!sendToAll && (
                  <>
                    {selectedRecipients.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-2 bg-blue-50 rounded-lg">
                        {selectedRecipients.map((recipient) => (
                          <Badge key={recipient.id} variant="secondary" className="flex items-center gap-1 pr-1 bg-white">
                            <span>{recipient.first_name} {recipient.last_name}</span>
                            <button type="button" onClick={() => toggleRecipient(recipient)} className="ml-1 hover:bg-slate-200 rounded-full p-0.5">
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input value={searchUser} onChange={(e) => setSearchUser(e.target.value)} placeholder="Rechercher un utilisateur..." className="pl-10" />
                    </div>

                    <ScrollArea className="h-48 border rounded-lg">
                      {filteredUsers.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">Aucun utilisateur trouvé</p>
                      ) : (
                        <div className="p-2 space-y-1">
                          {filteredUsers.map((u) => {
                            const isSelected = selectedRecipients.some(r => r.id === u.id);
                            return (
                              <div
                                key={u.id}
                                onClick={() => toggleRecipient(u)}
                                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-blue-100' : 'hover:bg-slate-100'}`}
                              >
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                                  {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <Avatar className="w-8 h-8">
                                  <AvatarFallback className={isSelected ? 'bg-blue-200' : 'bg-slate-200'}>
                                    {getInitials(u.first_name, u.last_name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{u.first_name} {u.last_name}</p>
                                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                                </div>
                                {u.role === 'admin' ? (
                                  <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                                    <ShieldCheck className="w-3 h-3 mr-1" />Super Admin
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-xs">
                                    <Shield className="w-3 h-3 mr-1" />Employé
                                  </Badge>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>
                  </>
                )}

                {sendToAll && (
                  <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-blue-700">Le document sera envoyé à {users.length} utilisateur(s)</span>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSendDialogOpen(false)}>Annuler</Button>
                <Button 
                  type="submit" 
                  disabled={submitting || (!sendToAll && selectedRecipients.length === 0)}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="send-document-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" />Envoyer ({sendToAll ? users.length : selectedRecipients.length})</>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
